'use client';
import React, { useEffect, useState } from 'react';
import type { LiveCourse } from '@/types/course';
import Image from 'next/image';
import CloseButton from '../Elements/CloseButton';
import Hr from '../Elements/Hr';
import { useUser } from '@clerk/nextjs';
import Button from '../Button/Button';
import Link from 'next/link';
import RegisteredMessage from '../Booking/RegisteredMessage';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import { ROUTES } from '@/config/routes';
import { T, useTranslate } from '@tolgee/react';
import { formatDate, formatTime } from '@/utils/helpers';
import { useHandleBooking } from '@/hooks/useHandleBooking';
import { useBookingService } from '@/hooks/useBookingService';
import { BookingSource, BookingTypes } from '@/types/booking';

type BookingModalProps = {
  course: LiveCourse;
  isOpen: boolean;
  onClose: () => void;
  initialBookingSuccess?: boolean;
};

const BookingModal: React.FC<BookingModalProps> = ({
  course,
  isOpen,
  onClose,
  initialBookingSuccess = false,
}) => {
  const { user } = useUser();
  const [bookingSuccessful, setBookingSuccessful] = useState(initialBookingSuccess);
  const [hasBooking, setHasBooking] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [participantsCount, setParticipantsCount] = useState(0);
  const { handleBooking, bookingId, isLoading } = useHandleBooking();
  const locale = useSelectedLanguage();
  const { t } = useTranslate();
  const { getParticipantsCount, hasActiveBooking } = useBookingService();

  useEffect(() => {
    const fetchParticipantsCount = async () => {
      if (course?.id) {
        const count = await getParticipantsCount(course.id.toString());
        setParticipantsCount(count);
      }
    };

    if (course?.id) {
      void fetchParticipantsCount();
    }
  }, [course?.id, getParticipantsCount]);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setTransitioning(true), 10);
    } else {
      setTransitioning(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const checkActiveBooking = async () => {
        const activeBooking = await hasActiveBooking(user?.id || '', course.id.toString());
        setHasBooking(activeBooking);
      };

      void checkActiveBooking();
    }
  }, [isOpen, course.id, user?.id, initialBookingSuccess]);

  if (!visible) return null;

  const handleBookingClick = async () => {
    const bookingParams = {
      externalId: course.expertExternalId,
      userId: user?.id,
      type: BookingTypes.COURSE,
      time: new Date(course.date),
      courseId: course.id,
      locale,
      source: BookingSource.USER,
    };

    await handleBooking(bookingParams);
    setBookingSuccessful(true);
  };

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex justify-center items-center z-50 transition-opacity duration-300 ${
        transitioning ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        className={`bg-white rounded-2xl max-w-2xl transform transition-transform duration-300 ${
          transitioning ? 'scale-100' : 'scale-95'
        }`}
      >
        {bookingSuccessful ? (
          <div className="text-center p-12 relative">
            <h2 className="text-4xl mb-5">Sehr gut!</h2>
            <p className="font-poppins text-base text-center leading-normal m-auto mb-12 w-3/4">
              <T>live_classes.live_class_signup_success.text.label</T>
            </p>
            <div className="w-1/4 m-auto">
              <Link
                href={ROUTES.BOOK_LIVE_COURSE(bookingId || hasBooking || '')}
                passHref
                prefetch={true}
              >
                <Button>
                  <T>live_classes.live_class_signup_success.button.continue.label</T>
                </Button>
              </Link>
            </div>
            <CloseButton className="absolute top-2.5 right-2.5" onClose={onClose} fill="black" />
          </div>
        ) : (
          <>
            <div className="bg-primary py-8 px-12 rounded-2xl text-white relative">
              <div className="">
                <div className="mt-4 w-56 h-36 relative m-auto mb-5">
                  <Image
                    src={course.imageSrc || ''}
                    alt={course.title}
                    className="object-cover rounded-lg"
                    fill
                  />
                </div>
                <h2 className="text-4xl text-center tracking-tightest">{course.title}</h2>
                <CloseButton className="absolute top-2.5 right-2.5" onClose={onClose} />
              </div>
            </div>
            <div className="px-12 pt-5">
              <div className="flex justify-between mb-4">
                <div className="flex-1">
                  <p className="text-sm text-primary uppercase mb-1 font-semibold">DATUM</p>
                  <p className="text-base">{formatDate(course.date, locale)}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-primary uppercase mb-1 font-semibold">
                    <T>live_classes.live_class_details.time.tag.label</T>
                  </p>
                  <p className="text-base">{formatTime(course.date, locale)}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-primary uppercase mb-1 font-semibold">
                    <T>live_classes.live_class_details.language.tag.label</T>
                  </p>
                  <p className="text-base">{course.languages}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-primary uppercase mb-1 font-semibold">
                    <T>live_classes.live_class_details.expert.tag.label</T>
                  </p>
                  <div className="text-base flex gap-2 items-center">
                    <Image
                      src={course.expertImage}
                      alt={course.expertName}
                      width={20}
                      height={20}
                      className="rounded-full object-cover w-5 h-5"
                    />
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {course.expertName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Hr />
            <div className="px-12 pb-8">
              <div className="relative">
                <div className={`max-h-[175px] overflow-x-hidden pb-5 overflow-y-auto`}>
                  <div className="text-base font-semibold mb-3 tracking-tightest">
                    <T>live_classes.live_class_details.description.tag.label</T>
                  </div>
                  <p
                    className="text-base font-poppins mb-5 leading-[1.4]"
                    dangerouslySetInnerHTML={{ __html: course.description }}
                  />

                  <p className="text-base font-semibold mb-3 tracking-tightest">
                    <T>live_classes.live_class_details.requirements.tag.label</T>
                  </p>
                  <ul className="eap-bullet-list text-base font-poppins mb-5">
                    <li>
                      <T>live_classes.live_class_details.requirements.live_class.bullet1.label</T>
                    </li>
                    <li>
                      <T>live_classes.live_class_details.requirements.live_class.bullet2.label</T>
                    </li>
                    <li>
                      <T>live_classes.live_class_details.requirements.live_class.bullet3.label</T>
                    </li>
                  </ul>

                  <div className="text-base font-semibold mb-3 tracking-tightest">
                    <T>live_classes.live_class_details.participants.tag.label</T>
                  </div>
                  <p className="text-base mb-5 font-poppins">
                    {participantsCount} Teilnehmer:innen
                  </p>

                  <span className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none block"></span>
                </div>
              </div>
              {!hasBooking && (
                <div className="mt-6">
                  <button
                    onClick={() => {
                      void handleBookingClick();
                    }}
                    className="bg-black text-white px-6 py-3 rounded-lg w-full"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? `${t('live_classes.live_class_details.button.register.label')}...`
                      : t('live_classes.live_class_details.button.register.label')}
                  </button>
                </div>
              )}

              {hasBooking && (
                <div className="mt-6 flex">
                  <Link
                    href={ROUTES.BOOK_LIVE_COURSE(hasBooking || '')}
                    className="w-52 inline-block mr-5"
                    passHref
                    prefetch={true}
                  >
                    <Button variant="primary">
                      <T>live_classes.live_class_details.button.show_booking.label</T>
                    </Button>
                  </Link>
                  <RegisteredMessage isDark={false} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
